'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { List, Map as ImmutableMap } from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import SenderAvatar from '../sender-avatar/SenderAvatar';
import { LAST_IN_GROUP, FIRST_IN_GROUP, LAST } from 'conversations-message-history/constants/messagePositionDescriptors';
import { AGENT, VISITOR } from 'conversations-message-history/constants/messageUses';
import PositionDescriptors from 'conversations-message-history/proptypes/PositionDescriptors';
import SenderMap from 'conversations-message-history/proptypes/SenderMap';
import SenderHeaderContent from '../headers/SenderHeaderContent';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { isSenderLoading } from 'conversations-message-history/senders/operators/isSenderLoading';
import { getTimestamp } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getOptionValue, getOptionLabel } from 'conversations-message-history/quick-reply/operators/quickReplyGetters';
import VizExButton from 'visitor-ui-component-library/button/VizExButton';
import { getQuickReply, getQuickReplyReplies, getQuickReplyAllowMultiSelect } from 'conversations-message-history/common-message-format/operators/cmfQuickReplyGetters';
import PrimaryMessage from '../primary-message/components/PrimaryMessage';
import PrimaryMessageAvatar from '../primary-message/components/PrimaryMessageAvatar';
import PrimaryMessageFooter from '../primary-message/components/PrimaryMessageFooter';
import PrimaryMessageHeader from '../primary-message/components/PrimaryMessageHeader';
import PrimaryMessageHeaderContent from '../primary-message/components/PrimaryMessageHeaderContent';
import QuickReplyGroup from '../quick-reply/QuickReplyGroup';
import { getHasQuickReplyOptions } from '../quick-reply/operators/getHasQuickReplyOptions';
import { MAX_QUICK_REPLY_BUTTONS } from '../quick-reply/constants/quickReplies';
import QuickReplySelect from '../quick-reply/QuickReplySelect';
import CommonMessageContent from './CommonMessageContent';
import VizExSmall from 'visitor-ui-component-library/typography/VizExSmall';
import { getSenderFromMessageSenders } from './operators/getSenderFromMessageSenders';
import { isFromVisitor } from 'conversations-message-history/common-message-format/operators/senderTypeComparators';
import { getFirstName } from 'conversations-message-history/senders/operators/getters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class CommonMessage extends PureComponent {
  constructor(props) {
    super(props);
    this.handleQuickReplySend = () => {
      const {
        message,
        publishMessage
      } = this.props;
      const {
        selectedQuickReply
      } = this.state;
      const text = getOptionLabel(selectedQuickReply);
      const quickReply = getQuickReply(message);
      const updatedQuickReply = quickReply.set('quickReplies', [selectedQuickReply]);
      publishMessage({
        text,
        quickReply: updatedQuickReply
      });
    };
    this.handleQuickReplySelect = ({
      target: {
        value
      }
    }) => {
      const {
        message
      } = this.props;
      const options = getQuickReplyReplies(message);
      const selected = options.filter(option => getOptionValue(option) === value).first();
      this.setState({
        selectedQuickReply: selected
      });
    };
    this.showQuickReplies = () => {
      const {
        hasUnansweredQuickReplyMessage,
        message,
        positionDescriptors
      } = this.props;
      const hasNotBeenAnswered = hasUnansweredQuickReplyMessage && positionDescriptors.get(LAST);
      return getHasQuickReplyOptions(message) && hasNotBeenAnswered;
    };
    this.showQuickReplySelection = () => {
      const {
        message
      } = this.props;
      const quickReplies = getQuickReplyReplies(message);
      const allowMultiSelect = getQuickReplyAllowMultiSelect(message);
      return this.showQuickReplies() && quickReplies.count() > MAX_QUICK_REPLY_BUTTONS && !allowMultiSelect;
    };
    this.renderQuickReplyGroup = () => {
      const {
        isThreadStarted,
        message,
        publishMessage,
        requiresExplicitConsentToProcess
      } = this.props;
      const quickReplies = getQuickReplyReplies(message);
      const allowMultiSelect = getQuickReplyAllowMultiSelect(message);
      return /*#__PURE__*/_jsx(PrimaryMessageFooter, {
        children: /*#__PURE__*/_jsx(QuickReplyGroup, {
          options: quickReplies,
          disabled: requiresExplicitConsentToProcess || !isThreadStarted,
          message: message,
          multiSelect: allowMultiSelect,
          publishMessage: publishMessage,
          onChange: this.handleQuickReplyUpdate
        })
      });
    };
    this.renderQuickReplySendButton = () => {
      const {
        isThreadStarted
      } = this.props;
      const {
        selectedQuickReply
      } = this.state;
      return /*#__PURE__*/_jsx(PrimaryMessageFooter, {
        children: /*#__PURE__*/_jsx(VizExButton, {
          disabled: !isThreadStarted || !getOptionValue(selectedQuickReply),
          onClick: this.handleQuickReplySend,
          size: "sm",
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "conversations-visitor-message-history.quickReply.selectionComplete"
          })
        })
      });
    };
    this.renderQuickReplySelect = () => {
      const {
        message,
        isThreadStarted,
        requiresExplicitConsentToProcess
      } = this.props;
      const {
        selectedQuickReply
      } = this.state;
      const quickReplies = getQuickReplyReplies(message);
      return /*#__PURE__*/_jsx("div", {
        className: "m-y-2",
        children: /*#__PURE__*/_jsx(QuickReplySelect, {
          quickReplyOptions: quickReplies,
          disabled: requiresExplicitConsentToProcess || !isThreadStarted,
          onChange: this.handleQuickReplySelect,
          value: getOptionValue(selectedQuickReply),
          placeholder: I18n.text('conversations-visitor-message-history.quickReply.selectionPlaceholder')
        })
      });
    };
    this.state = {
      selectedQuickReply: ImmutableMap()
    };
  }
  isSentFromVisitor() {
    return isFromVisitor(this.props.message);
  }
  get sender() {
    const {
      message,
      senders
    } = this.props;
    return getSenderFromMessageSenders(message, senders);
  }
  renderMessageHeader() {
    const {
      positionDescriptors
    } = this.props;
    const isFirstInGroup = positionDescriptors.includes(FIRST_IN_GROUP);
    if (!isFirstInGroup || this.isSentFromVisitor()) {
      return null;
    }
    const sender = this.sender;
    const isLoading = isSenderLoading(sender);
    return /*#__PURE__*/_jsxs(PrimaryMessageHeader, {
      children: [/*#__PURE__*/_jsx(PrimaryMessageAvatar, {
        children: /*#__PURE__*/_jsx(SenderAvatar, {
          sender: sender,
          isLoading: isLoading
        })
      }), /*#__PURE__*/_jsx(PrimaryMessageHeaderContent, {
        children: /*#__PURE__*/_jsx(SenderHeaderContent, {
          isSenderLoading: isLoading,
          sender: sender
        })
      })]
    });
  }
  renderTimeStamp() {
    const {
      message,
      positionDescriptors,
      locale
    } = this.props;
    const isLastInGroupMessage = positionDescriptors.includes(LAST_IN_GROUP);
    const timestamp = getTimestamp(message);
    if (!isLastInGroupMessage || !timestamp) {
      return null;
    }
    return /*#__PURE__*/_jsx(PrimaryMessageFooter, {
      children: /*#__PURE__*/_jsx(VizExSmall, {
        use: "help",
        children: new Date(timestamp).toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit'
        })
      })
    });
  }
  renderMessageFooter() {
    const {
      message
    } = this.props;
    const quickReplies = getQuickReplyReplies(message);
    const allowMultiSelect = getQuickReplyAllowMultiSelect(message);
    if (this.showQuickReplies() && this.showQuickReplySelection()) {
      return this.renderQuickReplySendButton();
    }
    if (this.showQuickReplies() && (quickReplies.count() <= MAX_QUICK_REPLY_BUTTONS || allowMultiSelect)) {
      return this.renderQuickReplyGroup();
    }
    return this.renderTimeStamp();
  }
  render() {
    const {
      fileAttachments,
      message,
      positionDescriptors,
      toggleOpen,
      mobile,
      hasUnansweredQuickReplyMessage
    } = this.props;
    const use = this.isSentFromVisitor() ? VISITOR : AGENT;
    return /*#__PURE__*/_jsxs(PrimaryMessage, {
      use: use,
      children: [this.renderMessageHeader(), /*#__PURE__*/_jsx(CommonMessageContent, {
        senderName: getFirstName(this.sender),
        fileAttachments: fileAttachments,
        isLastMessage: positionDescriptors.includes(LAST),
        message: message,
        quickReplySelect: this.showQuickReplySelection() && this.renderQuickReplySelect,
        mobile: mobile,
        toggleOpen: toggleOpen,
        hasUnansweredQuickReplyMessage: hasUnansweredQuickReplyMessage
      }), this.renderMessageFooter()]
    });
  }
}
CommonMessage.displayName = 'CommonMessage';
CommonMessage.propTypes = {
  fileAttachments: PropTypes.instanceOf(List),
  hasUnansweredQuickReplyMessage: PropTypes.bool,
  isThreadStarted: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  message: RecordPropType('CommonMessage').isRequired,
  mobile: PropTypes.bool,
  positionDescriptors: PositionDescriptors.isRequired,
  publishMessage: PropTypes.func,
  requiresExplicitConsentToProcess: PropTypes.bool.isRequired,
  senders: SenderMap,
  toggleOpen: PropTypes.func
};
export default CommonMessage;